import { ConfirmDialog } from 'components/confirm-dialog/confirm-dialog'
import { AppButton, Tooltip } from '@clickguard/design-system'
import { useStores } from 'hooks'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUiStore } from 'store'

export const DeleteAccount = () => {
	const {
		accountStore,
		assetStore,
		userStore: { user }
	} = useStores()
	const isLoading = useUiStore((s) => s.isLoading)
	const {
		account,
		deleteAccount,
		userAccessLevel,
		setAccount,
		wantsToSeeDemoData
	} = accountStore
	const { setAsset, setDemoMode, getAllAssets } = assetStore
	const navigate = useNavigate()
	const [open, setOpen] = useState<boolean>(false)

	const handleAccountDelete = async (id: string) => {
		try {
			const onSuccess = () => {
				setOpen(false)
			}
			const refreshedAccountsAfterDelete = await deleteAccount(id, onSuccess)
			const refreshedAccountsWithAssets = await getAllAssets()

			if (
				refreshedAccountsAfterDelete &&
				refreshedAccountsAfterDelete.length > 0
			) {
				const account = refreshedAccountsAfterDelete[0]
				setAccount(account)

				const selectedAccount = refreshedAccountsWithAssets?.find(
					(accountWithAsset) => accountWithAsset.id === account.id
				)
				let assetId = 'website-demo'

				if (selectedAccount) {
					const asset =
						selectedAccount.assets && selectedAccount.assets.length > 0
							? selectedAccount.assets[0]
							: null
					if (asset) {
						assetId = asset.id
						setAsset(asset)
					} else {
						setDemoMode(true)
					}
				} else {
					setAsset(null)
					setDemoMode(true)
				}

				navigate(`/workspace/${account.id}/asset/${assetId}`)
			} else {
				setAccount(null)
				setAsset(null)
				setDemoMode(true)
				navigate('/workspace/workspace-demo/asset/website-demo')
			}
		} catch (e) {
			return
		}
	}

	const isDeleteButtonDisabled =
		user && user.admin
			? false
			: account?.status === 'ACTIVE' || userAccessLevel(user!.id) !== 'OWNER'

	const deleteDisabledReason = wantsToSeeDemoData
		? 'This action is disabled when demo data is displayed'
		: isDeleteButtonDisabled && userAccessLevel(user?.id) !== 'OWNER'
		? 'Only the Owner of the workspace have permission for this action'
		: undefined

	return (
		<div className="mt-4 flex flex-col gap-4 rounded-xl border border-t-checkbox bg-white p-4 shadow-[0px_4px_8px_0px_rgba(16,24,40,0.03),0px_1px_2px_0px_rgba(16,24,40,0.02)]">
			<p className="text-[18px] leading-6 font-medium text-t-base-blue">
				Danger Zone
			</p>
			<div className="flex items-center justify-between gap-6">
				<p className="text-[16px] leading-[22px] font-medium text-[color:var(--error-ink,#f04438)]">
					Remove workspace and all it’s data
				</p>
				{deleteDisabledReason ? (
					<Tooltip content={deleteDisabledReason}>
						<AppButton
							variant="destructive"
							size="md"
							label="Delete"
							disabled={isDeleteButtonDisabled}
						/>
					</Tooltip>
				) : (
					<AppButton
						variant="destructive"
						size="md"
						label="Delete"
						disabled={isDeleteButtonDisabled}
						onClick={() => setOpen(true)}
					/>
				)}
				<ConfirmDialog
					open={open}
					onClose={() => setOpen(false)}
					onConfirm={() => handleAccountDelete(account!.id)}
					tone="danger"
					title={`Remove ${account?.name || 'Workspace'}`}
					description="Are you sure you want to remove this workspace and all it’s data? All websites and PPC accounts in this workspace will be removed as well. This action cannot be undone. Proceed?"
					confirmLabel="Delete"
					loading={isLoading('USER_REMOVE_ACCOUNT')}
				/>
			</div>
		</div>
	)
}
